import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ShoppingBag, Tag, Sparkles, Shield, X } from 'lucide-react';
import { useMarketplaceWS } from '@/hooks/useLaunchpadWS';

const KIND_ICON = { nft_booster: Sparkles, battle_insurance: Shield };
const KIND_LABEL = { nft_booster: 'NFT Booster', battle_insurance: 'Battle Insurance' };

function TradableSellerRow({ perk, onList }) {
  const [ask, setAsk] = useState(Math.max(50, perk.cost_points));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/referral_rewards/marketplace/list', { perk_id: perk.perk_id, ask_points: Number(ask) });
      toast.success('Listed');
      onList && onList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const Icon = KIND_ICON[perk.kind] || Tag;
  return (
    <div className="flex items-center gap-2 bg-[#0B0E11] border border-[#2B3139] p-2.5" data-testid={`sell-row-${perk.perk_id}`}>
      <Icon className="w-4 h-4 text-[#FCD535] shrink-0" />
      <span className="text-white text-sm flex-1 truncate">{perk.name}</span>
      <Input type="number" value={ask} onChange={e => setAsk(e.target.value)} className="w-20 h-8 bg-[#181A20] border-[#2B3139] text-white font-mono text-xs" min={50} max={perk.cost_points * 2} />
      <span className="text-[10px] font-mono text-[#848E9C]">pts</span>
      <Button onClick={submit} disabled={busy} className="h-8 px-2 text-xs bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11]" data-testid={`list-btn-${perk.perk_id}`}>List</Button>
    </div>
  );
}

export default function PerkMarketplace() {
  const [listings, setListings] = useState([]);
  const [feePct, setFeePct] = useState(5);
  const [tradable, setTradable] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [buying, setBuying] = useState(null);
  const [tab, setTab] = useState('buy');

  const load = async () => {
    try {
      const [pub, mine, active] = await Promise.all([
        api.get('/referral_rewards/marketplace/listings?limit=50'),
        api.get('/referral_rewards/marketplace/mine'),
        api.get('/referral_rewards/mine'),
      ]);
      setListings(pub.data.listings || []);
      setFeePct(pub.data.fee_pct || 5);
      setMyListings(mine.data.listings || []);
      // Tradable = active un-consumed nft_booster/battle_insurance perks NOT already listed
      const activeIds = new Set(pub.data.listings.filter(l => l.status === 'active').map(l => l.perk_id));
      const mineIds = new Set(mine.data.listings.filter(l => l.status === 'active').map(l => l.perk_id));
      const listed = new Set([...activeIds, ...mineIds]);
      setTradable((active.data.active || []).filter(p =>
        ['nft_booster', 'battle_insurance'].includes(p.kind) && !p.consumed && !listed.has(p.perk_id)
      ));
    } catch { /* silent */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  useMarketplaceWS(() => load());

  const buy = async (id) => {
    setBuying(id);
    try {
      const r = await api.post(`/referral_rewards/marketplace/${id}/buy`);
      toast.success(`Bought! · ${r.data.balance_points.toFixed(0)} pts left`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBuying(null);
  };
  const cancel = async (id) => {
    try {
      await api.post(`/referral_rewards/marketplace/${id}/cancel`);
      toast.success('Cancelled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <section className="border border-[#2B3139] bg-[#181A20] p-5 mt-6" data-testid="perk-marketplace">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <ShoppingBag className="w-5 h-5 text-[#FCD535]" />
        <h2 className="text-white font-semibold text-lg">Perk Marketplace</h2>
        <div className="text-[10px] font-mono text-[#848E9C]">Trade NFT Booster & Battle Insurance for pts · {feePct}% seller fee</div>
      </div>

      <div className="inline-flex border border-[#2B3139] p-0.5 bg-[#0B0E11] mb-4" data-testid="marketplace-tabs">
        {['buy', 'sell'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1 text-xs uppercase font-mono ${tab === t ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6]'}`}
            data-testid={`marketplace-tab-${t}`}>{t === 'buy' ? `Buy · ${listings.length}` : `Sell · ${tradable.length}`}</button>
        ))}
      </div>

      {tab === 'buy' && (
        <div>
          {listings.length === 0 ? (
            <div className="text-xs text-[#5E6673] py-6 text-center">No listings yet — check back soon or list one yourself.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {listings.map(l => {
                const Icon = KIND_ICON[l.perk_kind] || Tag;
                return (
                  <div key={l.listing_id} className="bg-[#0B0E11] border border-[#2B3139] p-3 flex items-center gap-3" data-testid={`listing-${l.listing_id}`}>
                    <Icon className="w-5 h-5 text-[#FCD535] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-semibold">{l.perk_name}</div>
                      <div className="text-[10px] font-mono text-[#848E9C]">by {l.seller_name} · cost {l.cost_points.toFixed(0)}p</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[#FCD535] font-mono font-semibold text-sm">{l.ask_points.toFixed(0)} pts</div>
                    </div>
                    <Button onClick={() => buy(l.listing_id)} disabled={buying === l.listing_id}
                      className="h-8 px-2 text-xs bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11]" data-testid={`buy-btn-${l.listing_id}`}>Buy</Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'sell' && (
        <div className="flex flex-col gap-2">
          {tradable.length === 0 && myListings.filter(l => l.status === 'active').length === 0 ? (
            <div className="text-xs text-[#5E6673] py-6 text-center">Redeem an NFT Booster or Battle Insurance to list it for sale.</div>
          ) : null}
          {tradable.map(p => <TradableSellerRow key={p.perk_id} perk={p} onList={load} />)}
          {myListings.filter(l => l.status === 'active').map(l => (
            <div key={l.listing_id} className="flex items-center gap-2 bg-[#0B0E11] border border-[#FCD535]/30 p-2.5" data-testid={`my-listing-${l.listing_id}`}>
              <Tag className="w-4 h-4 text-[#FCD535] shrink-0" />
              <span className="text-white text-sm flex-1 truncate">{l.perk_name}</span>
              <span className="text-[#FCD535] font-mono text-xs">{l.ask_points.toFixed(0)} pts</span>
              <span className="text-[10px] font-mono text-[#0ECB81]">LISTED</span>
              <Button onClick={() => cancel(l.listing_id)} className="h-7 w-7 p-0 bg-[#2B3139] hover:bg-[#3B4149]" data-testid={`cancel-${l.listing_id}`}>
                <X className="w-3.5 h-3.5 text-white" />
              </Button>
            </div>
          ))}
          {myListings.filter(l => l.status === 'sold').slice(0, 3).map(l => (
            <div key={l.listing_id} className="flex items-center gap-2 text-[10px] font-mono text-[#0ECB81] px-2 py-1">
              ✓ Sold {l.perk_name} · +{l.seller_received?.toFixed?.(0) ?? l.ask_points} pts
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
